'use client';

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Crown, Sparkles, Zap, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface FeatureLimitDialogProps {
  isOpen: boolean;
  onClose: () => void;
  feature: string;
  type: 'premium_required' | 'transcript_limit';
  remainingTranscripts?: number;
  transcriptLimit?: number;
}

const featureDescriptions: Record<string, { 
  title: string; 
  description: string; 
  icon: React.ReactNode;
  benefits: string[];
}> = {
  'Summary': {
    title: 'AI-Powered Summary',
    description: 'Get intelligent summaries that capture the key points and insights from your content.',
    icon: <Sparkles className="w-6 h-6 text-white" />,
    benefits: [
      'Smart key point extraction',
      'Multi-language summaries',
      'Custom summary styles',
      'Export & share options'
    ]
  },
  'Mindmap': {
    title: 'Visual Mind Maps',
    description: 'Transform complex content into beautiful, interactive visual mind maps.',
    icon: <Zap className="w-6 h-6 text-white" />,
    benefits: [
      'Automatic concept mapping',
      'Interactive visualization',
      'Customizable layouts',
      'Export as images'
    ]
  },
  'Vocabulary': {
    title: 'Smart Vocabulary',
    description: 'Advanced vocabulary analysis with definitions, examples, and practice exercises.',
    icon: <CheckCircle className="w-6 h-6 text-white" />,
    benefits: [
      'Intelligent word extraction',
      'Contextual definitions',
      'Practice exercises',
      'Progress tracking'
    ]
  },
  'Clean Read': {
    title: 'Clean Read Articles',
    description: 'Convert transcripts into polished, readable articles with perfect formatting.',
    icon: <Clock className="w-6 h-6 text-white" />,
    benefits: [
      'Professional formatting',
      'Improved readability',
      'Multiple export formats',
      'Custom styling options'
    ]
  }
};

const FeatureLimitContent: React.FC<{
  feature: string;
  type: 'premium_required' | 'transcript_limit';
  remainingTranscripts?: number;
  transcriptLimit?: number;
}> = ({ feature, type, remainingTranscripts, transcriptLimit }) => {
  const featureInfo = featureDescriptions[feature] || {
    title: feature,
    description: 'This premium feature enhances your learning experience.',
    icon: <Crown className="w-6 h-6 text-white" />,
    benefits: ['Enhanced functionality', 'Better user experience', 'Advanced features']
  };

  if (type === 'transcript_limit') {
    return (
      <div className="text-center space-y-6">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center">
          <AlertCircle className="w-8 h-8 text-white" />
        </div>
        
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Monthly Transcript Limit Reached
          </h3>
          <p className="text-gray-600 mb-4">
            You've used all <span className="font-semibold text-orange-600">{transcriptLimit}</span> of your free monthly transcriptions.
          </p>
          <p className="text-sm text-gray-500">
            Upgrade to Premium for unlimited transcriptions and access to all AI-powered features.
          </p>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
            <Crown className="w-5 h-5 text-purple-600 mr-2" />
            Premium Benefits
          </h4>
          <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              Unlimited transcripts
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              AI summaries
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              Mind maps
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              Vocabulary tools
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="text-center space-y-6">
      <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
        {featureInfo.icon}
      </div>
      
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Unlock {featureInfo.title}
        </h3>
        <p className="text-gray-600 mb-4">
          {featureInfo.description}
        </p>
        <p className="text-sm text-gray-500">
          This feature is exclusive to Premium subscribers.
        </p>
      </div>

      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4">
        <h4 className="font-semibold text-gray-900 mb-3">What you'll get:</h4>
        <div className="space-y-2">
          {featureInfo.benefits.map((benefit, index) => (
            <div key={index} className="flex items-center text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500 mr-3 flex-shrink-0" />
              {benefit}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export function FeatureLimitDialog({
  isOpen,
  onClose,
  feature,
  type,
  remainingTranscripts,
  transcriptLimit
}: FeatureLimitDialogProps) {
  const isMobile = useIsMobile();

  const handleUpgrade = () => {
    // Navigate to pricing page
    window.open('/pricing', '_blank');
    onClose();
  };

  const content = (
    <FeatureLimitContent 
      feature={feature}
      type={type}
      remainingTranscripts={remainingTranscripts}
      transcriptLimit={transcriptLimit}
    />
  );

  const actions = (
    <div className="flex flex-col sm:flex-row gap-3 w-full">
      <Button
        variant="outline"
        onClick={onClose}
        className="flex-1"
      >
        Maybe Later
      </Button>
      <Button
        onClick={handleUpgrade}
        className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
      >
        <Crown className="w-4 h-4 mr-2" />
        Upgrade to Premium
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="sr-only">
              {type === 'transcript_limit' ? 'Transcript Limit' : 'Premium Feature Required'}
            </DrawerTitle>
            <DrawerDescription className="sr-only">
              Upgrade to access premium features
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4 pb-4">
            {content}
          </div>
          <DrawerFooter>
            {actions}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="sr-only">
            {type === 'transcript_limit' ? 'Transcript Limit' : 'Premium Feature Required'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Upgrade to access premium features
          </DialogDescription>
        </DialogHeader>
        {content}
        <DialogFooter>
          {actions}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}